const DEFAULT_MODEL = "claude-3-haiku-20240307";
const DEFAULT_OPENAI_MODEL = "gpt-4o-mini";
const DEFAULT_MAX_TOKENS = 400;
const DEFAULT_REQUEST_TIMEOUT_MS = 45000;
const DEFAULT_STREAM_TIMEOUT_MS = 120000;

const PROVIDER_ROUTES = {
  claude: { route: "/api/claude", streamRoute: "/api/claude/stream" },
  openai: { route: "/api/openai", streamRoute: "/api/openai/stream" },
};

export function createClaudeClient(config = {}) {
  const endpoints = computeEndpoints(config);
  if (!endpoints.length) {
    throw new Error("No Claude endpoints configured");
  }

  const baseHeaders = { "content-type": "application/json", ...(config.headers || {}) };
  const requestTimeoutMs = config.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS;
  const streamTimeoutMs = config.streamTimeoutMs ?? DEFAULT_STREAM_TIMEOUT_MS;

  async function sendMessage(rawInput, options = {}) {
    const input = normalizeInput(rawInput);
    const provider = (options.provider || input.provider || "claude").toLowerCase();
    const routes = PROVIDER_ROUTES[provider] || PROVIDER_ROUTES.claude;
    const body = buildRequestBody(provider, input);
    const headers = { ...baseHeaders, ...(options.headers || {}) };
    const payload = JSON.stringify(body);

    const { response, endpoint, attempts } = await executeWithFallback({
      endpoints,
      path: routes.route,
      requestInit: {
        method: "POST",
        headers,
        body: payload,
        credentials: options.credentials ?? "omit",
        mode: options.mode ?? "cors",
      },
      signal: options.signal,
      timeoutMs: options.timeoutMs ?? requestTimeoutMs,
    });

    let json;
    try {
      json = await response.json();
    } catch (error) {
      const err = new Error("Unable to parse Claude response JSON");
      err.cause = error;
      err.endpoint = endpoint;
      err.attempts = attempts;
      throw err;
    }

    const data = normaliseChatResponse(provider, json, input);
    return { data, endpoint, attempts, raw: json };
  }

  async function streamMessage(rawInput, options = {}) {
    const input = normalizeInput(rawInput);
    const provider = (options.provider || input.provider || "claude").toLowerCase();
    const routes = PROVIDER_ROUTES[provider] || PROVIDER_ROUTES.claude;
    if (!routes.streamRoute) {
      throw new Error(`Streaming not supported for provider ${provider}`);
    }

    const body = buildRequestBody(provider, { ...input, stream: true });
    const headers = {
      ...baseHeaders,
      Accept: "text/event-stream",
      ...(options.headers || {}),
    };
    const payload = JSON.stringify(body);

    const { response, endpoint, attempts } = await executeWithFallback({
      endpoints,
      path: routes.streamRoute,
      requestInit: {
        method: "POST",
        headers,
        body: payload,
        credentials: options.credentials ?? "omit",
        mode: options.mode ?? "cors",
      },
      signal: options.signal,
      timeoutMs: options.timeoutMs ?? streamTimeoutMs,
      streaming: true,
    });

    options.onStart?.({ endpoint });

    const aggregate = {
      text: "",
      usage: null,
      cost: null,
      provider,
      events: [],
    };

    const usageTotals = { input_tokens: 0, output_tokens: 0 };

    const handleEvent = (evt) => {
      const parsed = interpretClaudeEvent(evt.event, evt.data);
      if (!parsed) return;
      aggregate.events.push({ event: evt.event, payload: parsed.raw ?? parsed });

      switch (parsed.kind) {
        case "delta": {
          if (parsed.text) {
            aggregate.text += parsed.text;
            options.onToken?.(parsed.text, {
              endpoint,
              provider,
              fullText: aggregate.text,
              raw: parsed.raw ?? parsed,
            });
          }
          break;
        }
        case "usage": {
          if (parsed.usage) {
            usageTotals.input_tokens = parsed.usage.input_tokens ?? usageTotals.input_tokens;
            usageTotals.output_tokens = parsed.usage.output_tokens ?? usageTotals.output_tokens;
            aggregate.usage = { ...usageTotals };
            options.onUsage?.(aggregate.usage, { endpoint, provider });
          }
          break;
        }
        case "done": {
          if (parsed.usage) {
            usageTotals.input_tokens = parsed.usage.input_tokens ?? usageTotals.input_tokens;
            usageTotals.output_tokens = parsed.usage.output_tokens ?? usageTotals.output_tokens;
            aggregate.usage = { ...usageTotals };
          }
          if (typeof parsed.cost === "number") {
            aggregate.cost = parsed.cost;
          }
          options.onDoneChunk?.({ endpoint, provider, data: parsed.raw ?? parsed });
          break;
        }
        case "error": {
          const err = new Error(parsed.message || "Claude stream error");
          err.data = parsed.raw ?? parsed;
          err.endpoint = endpoint;
          throw err;
        }
        default:
          break;
      }
    };

    try {
      await consumeSse(response, {
        signal: options.signal,
        onEvent: handleEvent,
      });
    } catch (error) {
      if (error?.name === "AbortError") {
        throw error;
      }
      options.onError?.(error, { endpoint, attempts });
      throw error;
    }

    if (!aggregate.usage && (usageTotals.input_tokens || usageTotals.output_tokens)) {
      aggregate.usage = { ...usageTotals };
    }

    options.onDone?.({
      text: aggregate.text,
      usage: aggregate.usage,
      cost: aggregate.cost,
      endpoint,
      provider,
    });

    return { data: aggregate, endpoint, attempts };
  }

  return {
    endpoints,
    sendMessage,
    streamMessage,
  };
}

const defaultClient = createClaudeClient();
export const defaultClaudeClient = defaultClient;

export function getConfiguredClaudeEndpoints() {
  return [...defaultClient.endpoints];
}

export async function sendClaudeMessage(input, options) {
  const result = await defaultClient.sendMessage(input, options);
  return result.data;
}

export async function streamClaudeMessage(input, handlers = {}) {
  const result = await defaultClient.streamMessage(input, handlers);
  return result.data;
}

export async function askClaude(input, maybeOptions) {
  let options = {};
  let legacyImage;

  if (typeof maybeOptions === "string") {
    legacyImage = maybeOptions;
  } else if (maybeOptions && typeof maybeOptions === "object") {
    options = maybeOptions;
    if (typeof options.image === "string") {
      legacyImage = options.image;
      delete options.image;
    }
  }

  let payload = input;
  if (legacyImage) {
    if (typeof input === "string") {
      payload = { prompt: input, image: legacyImage };
    } else if (input && typeof input === "object") {
      payload = { ...input, image: input.image ?? legacyImage };
    }
  }

  const result = await defaultClient.sendMessage(payload, options);
  return result.data?.text ?? "";
}

export async function askClaudeStream(input, onToken, maybeOptions = {}) {
  let handlers = {};
  let legacyImage;

  if (maybeOptions && typeof maybeOptions === "object") {
    handlers = { ...maybeOptions };
    if (typeof handlers.image === "string") {
      legacyImage = handlers.image;
      delete handlers.image;
    }
  } else if (typeof maybeOptions === "string") {
    legacyImage = maybeOptions;
  }

  let payload = input;
  if (legacyImage) {
    if (typeof input === "string") {
      payload = { prompt: input, image: legacyImage };
    } else if (input && typeof input === "object") {
      payload = { ...input, image: input.image ?? legacyImage };
    }
  }

  if (typeof onToken === "function") {
    handlers.onToken = (token, ctx) => onToken(token, ctx);
  }

  const result = await defaultClient.streamMessage(payload, handlers);
  return result.data?.text ?? "";
}

function computeEndpoints(config = {}) {
  const explicit = Array.isArray(config.endpoints) ? config.endpoints : [];
  const envList = parseEndpointListFromEnv(process.env.REACT_APP_CLAUDE_ENDPOINTS);

  const host = detectHostContext();

  const localBase =
    config.localBase ||
    process.env.REACT_APP_CLAUDE_LOCAL_BASE ||
    process.env.REACT_APP_API_BASE ||
    "http://localhost:3001";

  const remoteBase =
    config.remoteBase ||
    process.env.REACT_APP_CLAUDE_PROXY ||
    process.env.REACT_APP_CLAUDE_REMOTE_BASE ||
    process.env.REACT_APP_OPENAI_PROXY ||
    "https://api.moviefrenzy.ca";

  const preferLocal = config.preferLocal ?? host.isLocal;

  const includeLocalInProd =
    config.includeLocalInProd ?? process.env.REACT_APP_INCLUDE_LOCAL_PROXY === "1";

  const orderedDefaults = [];
  if (preferLocal) {
    orderedDefaults.push({ base: localBase, kind: "local", label: "local" });
    orderedDefaults.push({ base: remoteBase, kind: "cloud", label: "cloudflare" });
  } else {
    orderedDefaults.push({ base: remoteBase, kind: "cloud", label: "cloudflare" });
    if (includeLocalInProd) {
      orderedDefaults.push({ base: localBase, kind: "local", label: "local" });
    }
  }

  const merged = [...orderedDefaults, ...explicit, ...envList, ...(config.extraEndpoints || [])];

  const normalised = merged
    .map(normalizeEndpoint)
    .filter(Boolean)
    .filter((entry) => Boolean(entry.base));

  const seen = new Set();
  const deduped = [];
  for (const entry of normalised) {
    if (seen.has(entry.base)) continue;
    seen.add(entry.base);
    deduped.push(entry);
  }

  if (preferLocal) {
    deduped.sort((a, b) => {
      if (a.kind === "local" && b.kind !== "local") return -1;
      if (b.kind === "local" && a.kind !== "local") return 1;
      return 0;
    });
  } else {
    deduped.sort((a, b) => {
      if (a.kind === "cloud" && b.kind !== "cloud") return -1;
      if (b.kind === "cloud" && a.kind !== "cloud") return 1;
      return 0;
    });
  }

  if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
    try {
      console.debug("[Claude] endpoints:", deduped.map((e) => ({ base: e.base, kind: e.kind, label: e.label })));
    } catch (_) {
      // ignore console issues (e.g. unavailable in some envs)
    }
  }

  return deduped;
}

function parseEndpointListFromEnv(value) {
  if (!value || typeof value !== "string") return [];
  return value
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .map((base) => ({ base }));
}

function normalizeEndpoint(entry) {
  if (!entry) return null;
  if (typeof entry === "string") {
    return normalizeEndpoint({ base: entry });
  }
  const base = normalizeBase(entry.base || entry.url || entry.origin);
  if (!base) return null;
  return {
    base,
    label: entry.label || entry.name || inferEndpointKind(base),
    kind: entry.kind || inferEndpointKind(base),
  };
}

function normalizeInput(input, fallbackImage) {
  if (typeof input === "string") {
    return {
      prompt: input,
      image: typeof fallbackImage === "string" ? fallbackImage : undefined,
      maxTokens: DEFAULT_MAX_TOKENS,
      provider: "claude",
    };
  }
  if (!input || typeof input !== "object") {
    return {
      prompt: "",
      maxTokens: DEFAULT_MAX_TOKENS,
      provider: "claude",
    };
  }
  const prompt =
    input.prompt ||
    input.message ||
    (Array.isArray(input.messages) ? extractUserMessage(input.messages) : "");

  const attachments = Array.isArray(input.attachments)
    ? input.attachments
    : Array.isArray(input.images)
    ? input.images
    : undefined;

  const image =
    input.image || input.attach || pickFirstImageData(attachments) || undefined;

  return {
    prompt: prompt || "",
    model: input.model,
    maxTokens: input.maxTokens ?? input.max_tokens ?? DEFAULT_MAX_TOKENS,
    temperature: input.temperature,
    provider: input.provider,
    attachments,
    image,
    metadata: input.metadata,
    stream: input.stream,
    messages: input.messages,
    raw: input,
  };
}

function extractUserMessage(messages = []) {
  const lastUser = [...messages].reverse().find((m) => m?.role === "user");
  if (!lastUser) return "";
  if (typeof lastUser.content === "string") return lastUser.content;
  if (Array.isArray(lastUser.content)) {
    const textBlock = lastUser.content.find((block) => block?.type === "text");
    if (textBlock?.text) return textBlock.text;
  }
  return "";
}

function pickFirstImageData(items = []) {
  for (const item of items) {
    const data = extractDataUrl(item);
    if (data) return data;
  }
  return undefined;
}

function extractDataUrl(item) {
  if (!item) return undefined;
  if (typeof item === "string" && item.startsWith("data:")) return item;
  if (typeof item === "object") {
    if (typeof item.data === "string") return item.data;
    if (typeof item.preview === "string") return item.preview;
  }
  return undefined;
}

function collectImageDataList(primaryImage, attachments = []) {
  const seen = new Set();
  const results = [];

  const push = (value) => {
    if (typeof value !== "string" || !value.startsWith("data:")) return;
    if (seen.has(value)) return;
    seen.add(value);
    results.push(value);
  };

  if (primaryImage) push(primaryImage);
  if (Array.isArray(attachments)) {
    for (const att of attachments) {
      const dataUrl = extractDataUrl(att);
      if (dataUrl) push(dataUrl);
    }
  }

  return results;
}

function sanitizeAttachmentsForProxy(attachments = []) {
  if (!Array.isArray(attachments) || !attachments.length) return [];
  return attachments
    .map((att) => {
      if (typeof att === "string") {
        return att.startsWith("data:")
          ? { data: att }
          : null;
      }
      const data = extractDataUrl(att);
      if (!data) return null;
      return {
        data,
        type: att?.type || null,
        name: att?.name || null,
      };
    })
    .filter(Boolean);
}

function buildRequestBody(provider, input) {
  const prompt = (input.prompt || "").trim();
  const maxTokens = Number(input.maxTokens ?? DEFAULT_MAX_TOKENS) || DEFAULT_MAX_TOKENS;

  if (provider === "openai") {
    const messages = Array.isArray(input.messages) && input.messages.length
      ? input.messages
      : [{ role: "user", content: prompt }];

    return compactObject({
      model: input.model || DEFAULT_OPENAI_MODEL,
      messages,
      max_tokens: maxTokens,
      temperature: input.temperature,
      metadata: input.metadata,
    });
  }

  const body = {
    message: prompt,
    max_tokens: maxTokens,
    model: input.model || DEFAULT_MODEL,
    metadata: input.metadata,
  };

  const imageData = input.image || pickFirstImageData(input.attachments);
  const imageList = collectImageDataList(imageData, input.attachments);
  const sanitizedAttachments = sanitizeAttachmentsForProxy(input.attachments);

  if (imageData) {
    body.image = imageData;
  }
  if (imageList.length) {
    body.images = imageList;
  }
  if (sanitizedAttachments.length) {
    body.attachments = sanitizedAttachments;
  }

  if (input.stream) {
    body.stream = true;
  }

  return compactObject(body);
}

function compactObject(obj) {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined && value !== null)
  );
}

async function executeWithFallback({
  endpoints,
  path,
  requestInit,
  signal,
  timeoutMs,
  streaming,
}) {
  if (!endpoints || !endpoints.length) {
    throw new Error("No Claude endpoints configured");
  }
  if (signal?.aborted) {
    const abortErr = createAbortError();
    abortErr.attempts = [];
    throw abortErr;
  }

  const attempts = [];
  let lastError = null;

  for (const endpoint of endpoints) {
    const url = `${endpoint.base}${path}`;
    const init = cloneRequestInit(requestInit);
    const wrapped = wrapSignalWithTimeout(signal, timeoutMs);
    init.signal = wrapped.signal;

    try {
      const response = await fetch(url, init);
      wrapped.cleanup();

      if (!response.ok) {
        const errorPayload = await safeReadBody(response);
        attempts.push({ endpoint, status: response.status, ok: false, body: errorPayload });
        lastError = createHttpError(response.status, errorPayload, endpoint);
        if (shouldRetryStatus(response.status)) {
          continue;
        }
        lastError.attempts = attempts;
        throw lastError;
      }

      attempts.push({ endpoint, status: response.status, ok: true });
      return { response, endpoint, attempts };
    } catch (error) {
      wrapped.cleanup();
      if (error?.name === "AbortError") {
        error.attempts = attempts;
        throw error;
      }
      attempts.push({ endpoint, ok: false, error });
      lastError = error;
      continue;
    }
  }

  const finalError = lastError || new Error("All Claude endpoints failed");
  finalError.attempts = attempts;
  throw finalError;
}

function cloneRequestInit(init = {}) {
  const headers = init.headers instanceof Headers ? Object.fromEntries(init.headers.entries()) : { ...(init.headers || {}) };
  return { ...init, headers };
}

function wrapSignalWithTimeout(signal, timeoutMs) {
  if (!timeoutMs) {
    return { signal, cleanup: () => {} };
  }

  const controller = new AbortController();
  const timerId = setTimeout(() => {
    controller.abort(new DOMException("Request timed out", "AbortError"));
  }, timeoutMs);

  let abortListener;
  if (signal) {
    if (signal.aborted) {
      controller.abort(signal.reason ?? new DOMException("Aborted", "AbortError"));
    } else {
      abortListener = () => {
        controller.abort(signal.reason ?? new DOMException("Aborted", "AbortError"));
      };
      signal.addEventListener("abort", abortListener, { once: true });
    }
  }

  const cleanup = () => {
    clearTimeout(timerId);
    if (signal && abortListener) {
      signal.removeEventListener("abort", abortListener);
    }
  };

  return { signal: controller.signal, cleanup };
}

function shouldRetryStatus(status) {
  return [
    401,
    402,
    403,
    404,
    408,
    409,
    425,
    429,
    500,
    502,
    503,
    504,
  ].includes(Number(status));
}

async function safeReadBody(response) {
  try {
    return await response.text();
  } catch (error) {
    console.warn("Unable to read error body", error);
    return null;
  }
}

function createHttpError(status, body, endpoint) {
  const error = new Error(`HTTP ${status}`);
  error.status = status;
  error.body = body;
  if (endpoint) {
    error.endpoint = endpoint;
  }
  return error;
}

function createAbortError() {
  try {
    return new DOMException("Aborted", "AbortError");
  } catch (_) {
    const error = new Error("Aborted");
    error.name = "AbortError";
    return error;
  }
}

async function consumeSse(response, { onEvent, signal }) {
  if (!response.body || !response.body.getReader) {
    throw new Error("Streaming not supported in this environment");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const abortListener = signal
    ? () => {
        reader.cancel().catch(() => {});
      }
    : null;

  if (signal) {
    if (signal.aborted) {
      throw createAbortError();
    }
    signal.addEventListener("abort", abortListener, { once: true });
  }

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      buffer = processSseBuffer(buffer, onEvent);
    }
    buffer += decoder.decode();
    processSseBuffer(buffer, onEvent, true);
  } finally {
    if (signal && abortListener) {
      signal.removeEventListener("abort", abortListener);
    }
  }
}

function processSseBuffer(buffer, onEvent, flush = false) {
  let working = buffer;
  const separator = /\r?\n\r?\n/;
  let match;

  while ((match = separator.exec(working)) !== null) {
    const chunk = working.slice(0, match.index);
    working = working.slice(match.index + match[0].length);
    const evt = parseSseEvent(chunk);
    if (evt) onEvent(evt);
    separator.lastIndex = 0;
  }

  if (flush && working.trim()) {
    const evt = parseSseEvent(working);
    if (evt) onEvent(evt);
    return "";
  }

  return working;
}

function parseSseEvent(block) {
  if (!block) return null;
  const lines = block.split(/\r?\n/);
  let event = "message";
  const dataLines = [];
  let id;
  let retry;

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();
    if (!line.length || line.startsWith(":")) continue;
    if (line.startsWith("event:")) {
      event = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    } else if (line.startsWith("id:")) {
      id = line.slice(3).trim();
    } else if (line.startsWith("retry:")) {
      retry = Number(line.slice(6).trim());
    }
  }

  return {
    event,
    data: dataLines.join("\n"),
    id,
    retry,
  };
}

function interpretClaudeEvent(eventName, dataString) {
  const trimmed = typeof dataString === "string" ? dataString.trim() : "";
  if (!trimmed) return null;
  if (trimmed === "[DONE]") {
    return { kind: "done" };
  }

  const parsed = tryParseJSON(trimmed) ?? { text: trimmed };

  const textCandidate =
    parsed.text ||
    parsed.delta?.text ||
    parsed.delta?.content ||
    parsed.content?.text ||
    parsed.completion ||
    null;

  if (eventName === "delta") {
    return { kind: "delta", text: textCandidate, raw: parsed };
  }

  if (eventName === "done") {
    return {
      kind: "done",
      usage: parsed.usage || null,
      cost: parsed.cost_usd ?? parsed.cost ?? null,
      raw: parsed,
    };
  }

  if (eventName === "error") {
    const message = parsed.error?.message || parsed.message || textCandidate || "Stream error";
    return { kind: "error", message, raw: parsed };
  }

  if (parsed.type === "content_block_delta" && parsed.delta?.text) {
    return { kind: "delta", text: parsed.delta.text, raw: parsed };
  }

  if (parsed.type === "message_delta") {
    const usage = parsed.usage || parsed.delta?.usage;
    if (usage?.output_tokens_delta || usage?.input_tokens_delta) {
      return {
        kind: "usage",
        usage: {
          input_tokens: usage.input_tokens ?? usage.input_tokens_delta,
          output_tokens: usage.output_tokens ?? usage.output_tokens_delta,
        },
        raw: parsed,
      };
    }
    if (usage) {
      return { kind: "usage", usage, raw: parsed };
    }
  }

  if (parsed.type === "message_stop" || parsed.type === "content_block_stop") {
    return { kind: "done", raw: parsed };
  }

  if (parsed.type === "message_start" && parsed.message?.usage) {
    return { kind: "usage", usage: parsed.message.usage, raw: parsed };
  }

  if (parsed.error || parsed.type === "error" || parsed.kind === "error") {
    const message = parsed.error?.message || parsed.message || textCandidate || "Stream error";
    return { kind: "error", message, raw: parsed };
  }

  if (textCandidate) {
    return { kind: "delta", text: textCandidate, raw: parsed };
  }

  return { kind: "event", raw: parsed };
}

function normaliseChatResponse(provider, payload, input) {
  if (!payload || typeof payload !== "object") {
    return { text: "", usage: null, cost: null, provider, raw: payload };
  }

  const text =
    payload.text ??
    payload.reply ??
    payload.output ??
    payload.message ??
    payload.result ??
    extractTextFromContent(payload) ??
    "";

  const usage =
    payload.usage ||
    payload.cost?.usage ||
    payload.meta?.usage ||
    null;

  const cost =
    payload.cost_usd ??
    payload.cost?.est_usd ??
    payload.cost ??
    null;

  return {
    text,
    usage,
    cost,
    provider,
    input,
    raw: payload,
  };
}

function extractTextFromContent(payload) {
  const content = payload?.content;
  if (!content) return null;

  if (typeof content === "string") {
    return content;
  }

  if (Array.isArray(content)) {
    return content
      .flatMap((block) => {
        if (!block) return [];
        if (block.type === "text" && typeof block.text === "string") {
          return block.text;
        }
        if (Array.isArray(block.text)) {
          return block.text
            .filter((item) => typeof item === "string")
            .join("");
        }
        if (typeof block.text === "string") {
          return block.text;
        }
        return [];
      })
      .join("");
  }

  if (typeof content === "object") {
    return (
      (typeof content.text === "string" && content.text) ||
      (Array.isArray(content.text) && content.text.filter((item) => typeof item === "string").join("")) ||
      null
    );
  }

  return null;
}

function tryParseJSON(value) {
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch (_) {
    return null;
  }
}

function detectHostContext() {
  if (typeof window === "undefined" || !window.location) {
    return { hostname: "", isLocal: false };
  }
  const { hostname } = window.location;
  const isLocal =
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.endsWith(".local") ||
    /^10\./.test(hostname) ||
    /^192\.168\./.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname);
  return { hostname, isLocal };
}

function normalizeBase(base) {
  if (!base || typeof base !== "string") return null;
  const trimmed = base.trim();
  if (!trimmed) return null;
  return trimmed.replace(/\/$/, "");
}

function inferEndpointKind(base) {
  if (!base) return "cloud";
  if (/localhost|127\.0\.0\.1|10\.|192\.168\.|^\/\//.test(base)) {
    return "local";
  }
  return base.includes("moviefrenzy") ? "cloud" : "remote";
}
